/**
 * src/components/agent/ChatListItem.jsx — Single conversation row in the sidebar.
 *
 * Phase 82-03 — Chat System Unification, sidebar row.
 *
 * Renders: title (inline-editable when renaming) + optional project chip +
 * relative time. 44px min-height (button rules). Hover via e.currentTarget
 * inline style; reset in onMouseLeave. Right-click / long-press raises the
 * parent-provided onContextMenu callback with screen coords.
 */

import { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';

const LONG_PRESS_MS = 500;

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return '1d';
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

export default function ChatListItem({
  conversation,
  active = false,
  projectName = null,
  renaming = false,
  onClick,
  onContextMenu,
  onRenameCommit,
  onRenameCancel,
}) {
  const pressTimer = useRef(null);
  const inputRef = useRef(null);
  const [draftTitle, setDraftTitle] = useState(conversation?.title || '');

  useEffect(() => {
    if (renaming) {
      setDraftTitle(conversation?.title || '');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [renaming, conversation?.title]);

  const handleContextMenu = e => {
    if (!onContextMenu) return;
    e.preventDefault();
    onContextMenu({ x: e.clientX, y: e.clientY, conversation });
  };

  const handleTouchStart = e => {
    if (!onContextMenu) return;
    const touch = e.touches?.[0];
    const x = touch?.clientX || 0;
    const y = touch?.clientY || 0;
    pressTimer.current = setTimeout(() => {
      onContextMenu({ x, y, conversation });
    }, LONG_PRESS_MS);
  };

  const handleTouchEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleDotsClick = e => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    if (onContextMenu) {
      onContextMenu({ x: rect.right, y: rect.bottom, conversation });
    }
  };

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 44,
    padding: '6px 12px',
    borderRadius: 8,
    cursor: 'pointer',
    background: active ? 'var(--navy-05)' : 'transparent',
    borderLeft: active ? '3px solid var(--navy)' : '3px solid transparent',
    transition: 'background 120ms ease',
    userSelect: 'none',
  };

  const titleStyle = {
    flex: 1,
    fontSize: 14,
    fontWeight: active ? 600 : 500,
    color: 'var(--navy)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const chipStyle = {
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 6,
    background: 'var(--glass)',
    color: 'var(--tm)',
    maxWidth: 100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const timeStyle = {
    fontSize: 11,
    color: 'var(--tm)',
    minWidth: 28,
    textAlign: 'right',
  };

  const dotsBtnStyle = {
    minWidth: 44,
    minHeight: 44,
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: 'var(--tm)',
    cursor: 'pointer',
    borderRadius: 6,
    padding: 0,
    opacity: 0,
    transition: 'opacity 120ms ease',
  };

  const renameInputStyle = {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--navy)',
    background: 'var(--bg)',
    border: '1px solid var(--accent)',
    borderRadius: 6,
    padding: '4px 6px',
    outline: 'none',
    minHeight: 44,
    fontFamily: 'inherit',
  };

  if (renaming) {
    return (
      <div style={rowStyle} data-testid={`chat-list-item-${conversation.id}-renaming`}>
        <input
          ref={inputRef}
          value={draftTitle}
          onChange={e => setDraftTitle(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const clean = draftTitle.trim();
              if (clean) onRenameCommit?.(clean);
              else onRenameCancel?.();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onRenameCancel?.();
            }
          }}
          onBlur={() => {
            const clean = draftTitle.trim();
            if (clean && clean !== conversation.title) onRenameCommit?.(clean);
            else onRenameCancel?.();
          }}
          style={renameInputStyle}
          aria-label="Rename conversation"
        />
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      // 2026-04-20 — suppress iOS Safari's native long-press context
      // menu (Copy / Find Selection / Look Up / …) so the app's
      // Rename/Move/Archive/Delete menu isn't overlaid with a black
      // system bar. Reported on mobile: "I get, like, a pill overlaid
      // and I cannot rename it."
      style={{
        ...rowStyle,
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
      onClick={() => onClick?.(conversation)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(conversation);
        }
      }}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onMouseEnter={e => {
        if (!active) e.currentTarget.style.background = 'var(--glass)';
        const dots = e.currentTarget.querySelector('[data-dots]');
        if (dots) dots.style.opacity = '1';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = active ? 'var(--navy-05)' : 'transparent';
        const dots = e.currentTarget.querySelector('[data-dots]');
        if (dots) dots.style.opacity = '0';
      }}
      data-testid={`chat-list-item-${conversation.id}`}
    >
      <span style={titleStyle}>{conversation.title || 'Untitled'}</span>
      {projectName && <span style={chipStyle} title={projectName}>{projectName}</span>}
      <span style={timeStyle}>{relativeTime(conversation.updated_at || conversation.created_at)}</span>
      <button
        type="button"
        data-dots
        style={dotsBtnStyle}
        onClick={handleDotsClick}
        aria-label="Chat actions"
        data-testid={`chat-list-item-${conversation.id}-menu`}
      >
        <MoreVertical size={14} />
      </button>
    </div>
  );
}
